<template>
	<div class="flight-container">
		<div class="flight-container__left">
			<slot name="header"></slot>
			<div class="flight-container__flight">
				<slot name="outbound"></slot>
				<slot name="inbound"></slot>
			</div>
			<LinkButton
				v-if="withFlightDetails"
				class="flight-container__flight-details"
				label="Flugdetails"
				as="button"
				@click="emit('show-flight-details')"
			></LinkButton>
		</div>
		<div
			ref="right"
			:class="['flight-container__right', { 'flight-container__right--with-divider': verticalDivider }]"
		>
			<slot name="right"></slot>
		</div>
		<div :class="['flight-container__bottom', { 'flight-container__bottom--with-divider': horizontalDivider }]">
			<slot name="price"></slot>
			<div class="flight-container__button-row">
				<slot name="button"></slot>
			</div>
		</div>
	</div>
</template>

<script setup lang="ts">
import LinkButton from '@lmt-rpb/Atoms/LinkButton/LinkButton.vue';
import { useTemplateRef, computed } from 'vue';

type Props = {
	verticalDivider?: boolean;
	horizontalDivider?: boolean;
	withFlightDetails?: boolean;
};

const { withFlightDetails = true, verticalDivider = true, horizontalDivider = true } = defineProps<Props>();

const emit = defineEmits(['show-flight-details']);

const rightContainer = useTemplateRef('right');
const rightContainerDisplay = computed(() => {
	return rightContainer.value?.children.length === 0 ? 'none' : 'block';
});

const leftContainerSpan = computed(() => {
	return rightContainer.value?.children.length === 0 ? 'span 2 / span 2' : 'span 1 / span 1';
});
</script>

<style lang="scss" scoped>
.flight-container {
	display: grid;
	grid-template-columns: minmax(0, 55fr) minmax(0, 45fr);
	padding-block: 0.8rem;
	position: relative;

	&--loading {
		opacity: 0.15;
	}

	&__left {
		text-align: left;
		padding-right: 1.6rem;
		grid-column: span 2 / span 2;
	}

	&__right {
		display: none;
		padding-left: 1.6rem;

		&--with-divider {
			border-left: 0.1rem solid $color-border;
		}
	}

	&__flight {
		display: grid;
		grid-template-columns: repeat(2, minmax(0, 1fr));
		column-gap: 0.8rem;
		width: 100%;
	}

	&__flight-details {
		margin-top: 0.8rem;
		font-weight: $font-weight-semibold;
	}

	&__bottom {
		grid-column: span 2 / span 2;
		padding-top: 0.8rem;
		margin-top: 1.6rem;

		&--with-divider {
			border-top: 0.1rem solid $color-border;
		}
	}

	&__button-row {
		display: flex;

		:deep(button),
		:deep(a) {
			width: 100%;
			text-align: center;
		}
	}
}

@media screen and (width >= $breakpoint-small) {
	.flight-container {
		padding-block: 1.6rem;

		&__right {
			/* stylelint-disable-next-line value-keyword-case */
			display: v-bind(rightContainerDisplay);
		}

		&__left {
			/* stylelint-disable-next-line value-keyword-case */
			grid-column: v-bind(leftContainerSpan);
		}

		&__flight-details {
			margin-top: 1.6rem;
			font-weight: $font-weight-semibold;
		}

		&__button-row {
			display: flex;
			justify-content: flex-end;
			align-items: center;

			:deep(button),
			:deep(a) {
				width: unset;
				text-align: unset;
			}
		}
	}
}
</style>
